import React, { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Badge,
  Alert,
  AlertDescription,
} from './components/ui';
import {
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Tag,
} from 'lucide-react';

const ConflictResolutionPanel = ({ conflicts, onResolve, onCancel }) => {
  const [resolutions, setResolutions] = useState(
    conflicts.map(conflict => ({
      ...conflict,
      resolution: null,
    }))
  );

  const handleSelectResolution = (conflictIndex, resolution) => {
    const updated = [...resolutions];
    updated[conflictIndex].resolution = resolution;
    setResolutions(updated);
  };

  const handleMergeServices = (conflictIndex) => {
    const conflict = resolutions[conflictIndex];
    if (conflict.type !== 'services') return;

    // Merge all unique services across all sources
    const allServices = [];
    const serviceMap = new Map();

    conflict.sources.forEach(source => {
      source.value.forEach(service => {
        if (!serviceMap.has(service.id)) {
          serviceMap.set(service.id, service);
          allServices.push(service);
        }
      });
    });

    handleSelectResolution(conflictIndex, allServices);
  };

  const allResolved = resolutions.every(r => r.resolution !== null);

  const handleConfirm = () => {
    onResolve(resolutions);
  };

  const renderServiceConflict = (conflict, index) => {
    return (
      <Card key={index} className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-1" />
            <div className="flex-1">
              <h4 className="font-semibold text-lg mb-1">Service Conflict</h4>
              <p className="text-sm text-gray-600 mb-3">
                SKU: <span className="font-mono font-medium">{conflict.sku}</span>
              </p>

              <div className="space-y-3 mb-4">
                {conflict.sources.map((source, sourceIdx) => (
                  <div
                    key={sourceIdx}
                    className="border rounded-lg p-3 bg-white cursor-pointer hover:border-blue-500 transition-colors"
                    onClick={() => handleSelectResolution(index, source.value)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-sm font-medium">
                          Outbound #{source.outbound_id}
                        </span>
                        {source.outbound_name && (
                          <span className="text-xs text-gray-500 ml-2">
                            ({source.outbound_name})
                          </span>
                        )}
                      </div>
                      {resolutions[index].resolution === source.value && (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {source.value.length > 0 ? (
                        source.value.map((service, sIdx) => (
                          <Badge key={sIdx} variant="secondary" className="text-xs">
                            <Tag className="w-3 h-3 mr-1" />
                            {service.name}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 italic">No services</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleMergeServices(index)}
                  className="w-full"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Merge All Services (Union)
                </Button>
                {resolutions[index].resolution &&
                  !conflict.sources.some(s => s.value === resolutions[index].resolution) && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs">
                    <p className="font-medium text-green-800 mb-1">Merged Services:</p>
                    <div className="flex flex-wrap gap-1">
                      {resolutions[index].resolution.map((service, sIdx) => (
                        <Badge key={sIdx} variant="secondary" className="text-xs">
                          {service.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderExpiryConflict = (conflict, index) => {
    return (
      <Card key={index} className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-1" />
            <div className="flex-1">
              <h4 className="font-semibold text-lg mb-1">Expiry Date Conflict</h4>
              <p className="text-sm text-gray-600 mb-3">
                SKU: <span className="font-mono font-medium">{conflict.sku}</span>
              </p>

              <div className="space-y-2 mb-3">
                {conflict.sources.map((source, sourceIdx) => (
                  <div
                    key={sourceIdx}
                    className="border rounded-lg p-3 bg-white cursor-pointer hover:border-blue-500 transition-colors"
                    onClick={() => handleSelectResolution(index, source.value)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium">
                          {source.value ? (
                            new Date(source.value).toLocaleDateString()
                          ) : (
                            <span className="text-gray-400 italic">No expiry date</span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500">
                          (Outbound #{source.outbound_id})
                        </span>
                      </div>
                      {resolutions[index].resolution === source.value && (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-xs">
                  <strong>Tip:</strong> For food/perishable items, selecting the earliest date is
                  usually the safest choice for compliance and inventory management.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          <h3 className="font-semibold text-lg">Resolve Conflicts</h3>
        </div>
        <p className="text-sm text-gray-700">
          {conflicts.length} conflict(s) detected. Please select the preferred value for each
          conflict below.
        </p>
        <div className="mt-2 text-xs text-gray-600">
          Resolved: {resolutions.filter(r => r.resolution !== null).length} / {conflicts.length}
        </div>
      </div>

      <div className="max-h-[500px] overflow-y-auto space-y-3 pr-2">
        {resolutions.map((conflict, index) =>
          conflict.type === 'services'
            ? renderServiceConflict(conflict, index)
            : renderExpiryConflict(conflict, index)
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={!allResolved}>
          {allResolved ? (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Continue to Preview
            </>
          ) : (
            <>Resolve All Conflicts ({resolutions.filter(r => r.resolution !== null).length}/{conflicts.length})</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ConflictResolutionPanel;
